import { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

import { Box, Flex, Skeleton, Stack } from '@chakra-ui/react';

import Breadcrumbs from 'components/common/Breadcrumbs';
import CourseHeader from 'components/course/CourseHeader';
import QuestItem from 'components/quest/QuestItem';

import { fetchProgressionByCourseIdAction } from 'store/modules/progression/progression-actions';
import { subquestActions } from 'store/modules/subquest/subquest-slice';
import { handleErrorAndNavigate, createErrorInfo } from 'utils/errorHandler';

function ClassCoursePage() {
  const { classId, courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isCourseLoading, setIsCourseLoading] = useState(false);
  const [isQuestLoading, setIsQuestLoading] = useState(true);

  const [classObject, setClassObject] = useState(null);
  const [course, setCourse] = useState(null);
  const [quests, setQuests] = useState([]);

  // Configuration for ErrorDisplay component - memoized to prevent useEffect dependency changes
  const classCourseEntityConfig = useMemo(
    () => ({
      name: 'Course',
      homeRoute: `/classes/${classId}`,
      courseId: courseId ? parseInt(courseId, 10) : undefined, // Add course ID for enhanced class access links
    }),
    [classId, courseId]
  );

  useEffect(() => {
    dispatch(subquestActions.enableSubQuestScoreDisplay());

    return () => {
      dispatch(subquestActions.disableSubQuestScoreDisplay());
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchCourseById = async () => {
      setIsCourseLoading(true);
      try {
        const response = await axios.get(
          `/classes/${classId}/courses/${courseId}`
        );
        setCourse(response.data.course);
        setClassObject(response.data.class);
        document.title = `Skill Quest | Class -  ${response.data.course.name}`;
      } catch (error) {
        // Create standardized error info
        const errorInfo = createErrorInfo(
          error,
          'Course Not Found',
          'The course you are looking for does not exist in this class or may have been removed.'
        );

        // Handle error and navigate using utility function
        handleErrorAndNavigate(
          dispatch,
          navigate,
          location,
          errorInfo,
          classCourseEntityConfig
        );
      }
      setIsCourseLoading(false);
    };
    fetchCourseById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, courseId, navigate, dispatch, classCourseEntityConfig]);

  useEffect(() => {
    if (course) {
      const fetchCourseQuests = async () => {
        setIsQuestLoading(true);
        try {
          const response = await axios.get(
            `/classes/${classId}/courses/${course.id}/quests`
          );
          setQuests(response.data);
        } catch {
          setQuests([]);
        }
        setIsQuestLoading(false);
      };

      dispatch(fetchProgressionByCourseIdAction(course.id));

      fetchCourseQuests();
    }
  }, [course, classId, dispatch]);

  return (
    <Flex minH="100vh" ml={{ base: 0, md: 20 }}>
      <Box flex="1" ml={2} p="5">
        <Flex direction={{ base: 'column', lg: 'row' }}>
          <Box flex="1">
            <Breadcrumbs
              mb={4}
              links={[
                { name: 'Home', path: '/' },
                { name: classObject?.name, path: `/classes/${classId}` },
                {
                  name: course?.name,
                  path: `/classes/${classId}/courses/${courseId}`,
                },
              ]}
            />
            <Flex direction={{ base: 'column', sm: 'row' }}>
              <CourseHeader
                isLoading={isCourseLoading}
                course={course}
                flex="1"
              />
            </Flex>

            <Box mt={7}>
              {isQuestLoading ? (
                <Stack>
                  <Skeleton mb={6} height="72px" borderRadius="md" />
                  <Skeleton mb={6} height="72px" borderRadius="md" />
                  <Skeleton mb={6} height="72px" borderRadius="md" />
                  <Skeleton mb={6} height="72px" borderRadius="md" />
                </Stack>
              ) : (
                <Box>
                  {quests.map((quest) => (
                    <QuestItem
                      key={quest.id}
                      classId={parseInt(classId, 10)}
                      courseId={parseInt(courseId, 10)}
                      quest={quest}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
}

export default ClassCoursePage;
